export interface ReportRangeOption {
  /** Months a report date range may span. Stored in the plan as `maxReportRangeMonths`. */
  value: number;
  label: string;
}

/** Report date range a plan can allow, in months. Must match the backend `REPORT_RANGE_MONTH_OPTIONS`. */
export const REPORT_RANGE_OPTIONS: readonly ReportRangeOption[] = [
  { value: 6, label: "6 months" },
  { value: 12, label: "1 year" },
  { value: 24, label: "2 years" },
  { value: 36, label: "3 years" },
  { value: 60, label: "5 years" },
  { value: 120, label: "10 years" },
  { value: 180, label: "15 years" },
  { value: 240, label: "20 years" },
];

/** Range of a plan that sets no `maxReportRangeMonths` (1 year, the backend default). */
export const DEFAULT_REPORT_RANGE_MONTHS = 12;

/** Plan limit key of the report date range, inside the Report module limits. */
export const REPORT_RANGE_LIMIT_KEY = "maxReportRangeMonths";

/** `code` of the backend 400 for a report range longer than the plan allows. Must match the backend constant. */
export const REPORT_DATE_RANGE_EXCEEDED_CODE = "REPORT_DATE_RANGE_EXCEEDED";

/** `6 months`, `1 year`, `2 years`: the option label, or a plain months / years text for other values. */
export const formatReportRangeLabel = (months: number): string => {
  const option = REPORT_RANGE_OPTIONS.find((item) => item.value === months);
  if (option) return option.label;
  if (months % 12 === 0) return `${months / 12} years`;

  return `${months} months`;
};

export const isReportRangeMonths = (value: unknown): value is number =>
  typeof value === "number" && REPORT_RANGE_OPTIONS.some((option) => option.value === value);

/** Text shown when a report range is longer than the plan allows. */
export const formatReportRangeExceededMessage = (months: number): string =>
  `Your plan allows reports of up to ${formatReportRangeLabel(months)}. Please choose a shorter date range.`;

/** A report longer than this many months asks for confirmation before it is generated. */
export const LARGE_REPORT_RANGE_MONTHS = 24;

/** First month of the financial year (0 = January), April as in India. */
export const FINANCIAL_YEAR_START_MONTH = 3;
